import { getRuntimeConfig } from './runtimeConfig';

const SYNTHETIC_PROJECT = 'featherglass-dev-2026';
const SYNTHETIC_RUN_ORIGIN = 'https://featherglass-dev-838082890481.us-central1.run.app';
const SYNTHETIC_RUN_HOST = 'featherglass-dev-838082890481.us-central1.run.app';
export const STATIC_HTML_ANALYTICS_ID = 'G-K0T7P7KWE9';
export const REACT_ANALYTICS_ID = 'G-2BDD759GEL';

let pendingEmailAction = null;
let pendingPasswordResetCode = null;

export function isSyntheticDevRuntime(config = getRuntimeConfig()) {
  return ['dev', 'local'].includes(config?.appEnv) && config?.firebase?.projectId === SYNTHETIC_PROJECT;
}

function syntheticAppOrigin(config) {
  if (!isSyntheticDevRuntime(config)) return null;
  const origins = config.appEnv === 'local'
    ? ['http://localhost:3000'] : [SYNTHETIC_RUN_ORIGIN, 'https://dev.featherglass.com'];
  if (!origins.includes(config.appUrl)) {
    throw new Error('Synthetic Dev email requires its exact configured application origin');
  }
  return config.appUrl;
}

const FEATHERGLASS_ENVIRONMENT_HOSTS = new Set([
  "dev.featherglass.com",
  "staging.featherglass.com",
  "secure.featherglass.com",
]);

function isLocalHostname(hostname) {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
}
function isApprovedVerificationOrigin(value) {
  try {
    const url = new URL(value);
    if (FEATHERGLASS_ENVIRONMENT_HOSTS.has(url.hostname)) {
      return url.protocol === "https:";
    }
    return isLocalHostname(url.hostname) && (url.protocol === "http:" || url.protocol === "https:");
  } catch {
    return false;
  }
}

export function isSensitiveAuthRoute(pathname = typeof window !== 'undefined' ? window.location?.pathname : '') {
  const path = String(pathname || '');
  return path === '/__/auth/action' || path === '/reset-password' || path.startsWith('/invite/');
}

function containsActionParameter(rawValue) {
  const text = String(rawValue || '').replace(/^[?#]/, '');
  if (!text) return false;
  const params = new URLSearchParams(text);
  return params.has('oobCode') || params.has('mode') || /(?:^|[&#?])(?:oobCode|mode)=/i.test(String(rawValue || ''));
}

function scrubBrowserLocation(location, history, cleanUrl) {
  history?.replaceState?.(null, '', cleanUrl);
  if (location && (typeof window === 'undefined' || location !== window.location)) {
    try {
      const parsed = new URL(cleanUrl, 'https://placeholder.local');
      location.pathname = parsed.pathname;
      location.search = parsed.search;
      location.hash = parsed.hash;
    } catch {
      // Ignore read-only location properties on test doubles.
    }
  }
}

/**
 * Consume synthetic-Dev email action parameters from the URL fragment into
 * module memory and immediately scrub the URL from browser history before any
 * analytics or navigation can run.
 */
export function captureEmailActionFromLocation({
  location = typeof window !== 'undefined' ? window.location : null,
  history = typeof window !== 'undefined' ? window.history : null,
  config = getRuntimeConfig(),
} = {}) {
  if (!location) return null;
  const pathname = location.pathname || '/';
  const rawHash = typeof location.hash === 'string' ? location.hash.replace(/^#/, '') : '';
  const rawSearch = typeof location.search === 'string' ? location.search.replace(/^\?/, '') : '';

  if (!isSyntheticDevRuntime(config)) {
    if (containsActionParameter(rawHash)) {
      scrubBrowserLocation(location, history, `${pathname}${location.search || ''}`);
    }
    return null;
  }

  // Ensure configured origin is valid before accepting or scrubbing synthetic actions.
  syntheticAppOrigin(config);

  if (pathname === '/reset-password') {
    if (rawHash || rawSearch) {
      scrubBrowserLocation(location, history, '/reset-password');
      pendingPasswordResetCode = null;
    }
    return null;
  }

  if (pathname !== '/__/auth/action') {
    if (containsActionParameter(rawHash) || containsActionParameter(rawSearch)) {
      scrubBrowserLocation(location, history, pathname);
      pendingEmailAction = null;
      pendingPasswordResetCode = null;
    }
    return null;
  }

  if (rawSearch) {
    scrubBrowserLocation(location, history, '/__/auth/action');
    pendingEmailAction = { mode: null, actionCode: null, continueUrl: null, invalid: true };
    pendingPasswordResetCode = null;
    return pendingEmailAction;
  }

  if (!rawHash) {
    return pendingEmailAction;
  }

  scrubBrowserLocation(location, history, '/__/auth/action');
  const params = new URLSearchParams(rawHash);
  const mode = params.get('mode');
  const actionCode = params.get('oobCode');
  const continueUrl = params.get('continueUrl');
  const apiKey = params.get('apiKey');
  const lang = params.get('lang');
  const valid =
    params.getAll('mode').length === 1
    && typeof mode === 'string'
    && /^[A-Za-z]{1,64}$/.test(mode)
    && params.getAll('oobCode').length === 1
    && typeof actionCode === 'string'
    && /^[A-Za-z0-9_-]{1,2048}$/.test(actionCode)
    && params.getAll('apiKey').length <= 1
    && (apiKey === null || (/^[A-Za-z0-9_-]{1,256}$/.test(apiKey) && (!config?.firebase?.apiKey || apiKey === config.firebase.apiKey)))
    && params.getAll('lang').length <= 1
    && (lang === null || /^[a-zA-Z]{2,3}(?:-[a-zA-Z0-9]{2,8}){0,3}$/.test(lang));

  pendingPasswordResetCode = null;
  pendingEmailAction = valid
    ? { mode, actionCode, continueUrl }
    : { mode: null, actionCode: null, continueUrl, invalid: true };
  return pendingEmailAction;
}

export function consumeEmailAction({
  searchParams = null,
  location = typeof window !== 'undefined' ? window.location : null,
  history = typeof window !== 'undefined' ? window.history : null,
  config = getRuntimeConfig(),
} = {}) {
  captureEmailActionFromLocation({ location, history, config });

  if (isSyntheticDevRuntime(config)) {
    const captured = pendingEmailAction;
    pendingEmailAction = null;
    if (searchParams && (searchParams.get('oobCode') || searchParams.get('mode'))) {
      pendingPasswordResetCode = null;
      return { mode: null, actionCode: null, continueUrl: null, isSynthetic: true };
    }
    if (!captured || captured.invalid) {
      return {
        mode: null,
        actionCode: null,
        continueUrl: captured?.continueUrl || null,
        isSynthetic: true,
      };
    }
    return {
      mode: captured.mode,
      actionCode: captured.actionCode,
      continueUrl: captured.continueUrl,
      isSynthetic: true,
    };
  }

  return {
    mode: searchParams?.get('mode') || null,
    actionCode: searchParams?.get('oobCode') || null,
    continueUrl: searchParams?.get('continueUrl') || null,
    isSynthetic: false,
  };
}

export function stashPasswordResetCode(actionCode, { config = getRuntimeConfig() } = {}) {
  if (!isSyntheticDevRuntime(config)) {
    pendingPasswordResetCode = null;
    return false;
  }
  if (typeof actionCode !== 'string' || !/^[A-Za-z0-9_-]{1,2048}$/.test(actionCode)) {
    pendingPasswordResetCode = null;
    return false;
  }
  pendingPasswordResetCode = actionCode;
  return true;
}

export function consumePasswordResetCode({
  searchParams = null,
  location = typeof window !== 'undefined' ? window.location : null,
  history = typeof window !== 'undefined' ? window.history : null,
  config = getRuntimeConfig(),
} = {}) {
  if (isSyntheticDevRuntime(config)) {
    const rawHash = typeof location?.hash === 'string' ? location.hash.replace(/^#/, '') : '';
    const rawSearch = typeof location?.search === 'string' ? location.search.replace(/^\?/, '') : '';
    if (rawHash || rawSearch) {
      scrubBrowserLocation(location, history, location?.pathname || '/reset-password');
      pendingPasswordResetCode = null;
      return null;
    }
    if (searchParams?.get('oobCode')) {
      pendingPasswordResetCode = null;
      return null;
    }
    const code = pendingPasswordResetCode;
    pendingPasswordResetCode = null;
    return code;
  }

  return searchParams?.get('oobCode') || null;
}

export function shouldEnableAnalytics({
  config = getRuntimeConfig(),
  location = typeof window !== 'undefined' ? window.location : null,
} = {}) {
  if (!location || !location.hostname || isLocalHostname(location.hostname)) return false;
  if (!config || config.appEnv === 'local') return false;
  if (isSyntheticDevRuntime(config) || location.hostname === SYNTHETIC_RUN_HOST) return false;
  if (isSensitiveAuthRoute(location.pathname)) return false;
  if (containsActionParameter(location.search) || containsActionParameter(location.hash)) return false;
  return true;
}

export function initializeBrowserAnalytics({
  reactGA = null,
  measurementId = REACT_ANALYTICS_ID,
  htmlMeasurementId = STATIC_HTML_ANALYTICS_ID,
  config = getRuntimeConfig(),
  location = typeof window !== 'undefined' ? window.location : null,
  history = typeof window !== 'undefined' ? window.history : null,
  windowObj = typeof window !== 'undefined' ? window : null,
} = {}) {
  captureEmailActionFromLocation({ location, history, config });

  if (!shouldEnableAnalytics({ config, location })) {
    if (windowObj) {
      windowObj[`ga-disable-${htmlMeasurementId}`] = true;
      windowObj[`ga-disable-${measurementId}`] = true;
    }
    return false;
  }

  if (windowObj) {
    delete windowObj[`ga-disable-${htmlMeasurementId}`];
    delete windowObj[`ga-disable-${measurementId}`];
  }
  reactGA?.initialize?.(measurementId);
  if (windowObj && htmlMeasurementId) {
    if (typeof windowObj.gtag === 'function') {
      windowObj.gtag('config', htmlMeasurementId);
    } else if (Array.isArray(windowObj.dataLayer)) {
      windowObj.dataLayer.push(['config', htmlMeasurementId]);
    }
  }
  return true;
}

export function sanitizeAuthActionError(error, fallback = 'auth/action-error') {
  if (typeof error?.code === 'string' && /^[a-z0-9_-]+\/[a-z0-9_-]+$/i.test(error.code)) {
    return error.code;
  }
  const raw = typeof error === 'string' ? error : (error?.message || '');
  const withoutControls = Array.from(String(raw || ''), (ch) => {
    const code = ch.charCodeAt(0);
    return code <= 0x1f || code === 0x7f ? ' ' : ch;
  }).join('');
  const cleaned = withoutControls
    .replace(/https?:\/\/\S+/gi, '[REDACTED_URL]')
    .replace(/\b(?:oobCode|apiKey|continueUrl|token)=[^&\s#]+/gi, '[REDACTED_PARAM]')
    .replace(/\b(?:SG\.[A-Za-z0-9._-]+|AIza[A-Za-z0-9_-]+)\b/g, '[REDACTED_KEY]')
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[REDACTED_EMAIL]')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);
  return cleaned || fallback;
}

export function resetEmailActionHandoffForTesting() {
  pendingEmailAction = null;
  pendingPasswordResetCode = null;
}

export function buildVerificationContinueUrl(origin = window.location.origin) {
  return new URL("/?verified=true", origin).toString();
}

/**
 * Resolve the destination carried by Firebase's shared email-action handler.
 * Only the three deployed Featherglass environments and local development may
 * receive the browser after verification; all other origins fail closed to the
 * environment currently handling the action.
 */
export function resolveVerificationReturnUrl(
  continueUrl,
  currentOrigin = window.location.origin,
  config = getRuntimeConfig()
) {
  const isolatedOrigin = syntheticAppOrigin(config);
  if (isolatedOrigin) {
    // In the isolated plane, never return an action to a shared project's app.
    return buildVerificationContinueUrl(isolatedOrigin);
  }
  if (isApprovedVerificationOrigin(continueUrl)) {
    const targetOrigin = new URL(continueUrl).origin;
    return buildVerificationContinueUrl(targetOrigin);
  }

  return buildVerificationContinueUrl(currentOrigin);
}

/**
 * Return to the sign-in screen for the environment where the email action
 * started, without claiming that a failed or unrelated action verified email.
 */
export function resolveEmailActionSignInUrl(
  continueUrl,
  currentOrigin = window.location.origin,
  config = getRuntimeConfig()
) {
  const verificationReturnUrl = resolveVerificationReturnUrl(continueUrl, currentOrigin, config);
  return new URL("/", verificationReturnUrl).toString();
}
